import { useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import UiContext from "../../../../contexts/UiContext";
import IBreadcrumbs from "../../../../models/IBreadcrumbs";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import EmailBlockTypeEnum from "../../../../models/EmailBlockTypeEnum";
import EmailEventType from "../../../../models/EmailEventType";
import IEmailBlock, { IEmailLeafBlock } from "../../../../models/IEmailBlock";
import IEmailTemplateSummaryDTO from "../../../../models/DTO/IEmailTemplateSummaryDTO";
import { useEmailTemplateEditor } from "../../../../hooks/business/useEmailTemplateEditor";
import { IEmailTemplateKey } from "../../../../services/EmailTemplateService";
import {
    addToColumn,
    createBlock,
    duplicateBlock,
    findBlock,
    insertBlock,
    removeBlock,
    updateBlock,
} from "../../../../services/EmailDocumentUtils";
import Breadcrumbs from "../../../shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import ActivityIndicator from "../../../shared/ActivityIndicator/ActivityIndicator";
import EmailTemplateList from "./EmailTemplateList";
import BlockPalette from "./BlockPalette";
import BlockCanvas from "./BlockCanvas";
import BlockPropertyPanel from "./BlockPropertyPanel";
import ThemePanel from "./ThemePanel";
import EmailPreviewPane from "./EmailPreviewPane";
import MergeVarPicker from "./MergeVarPicker";
import OwTranslate from "../../../shared/OwTranslate/OwTranslate";
import "./EmailTemplateEditor.scss";

const keyFromSearch = (params: URLSearchParams): IEmailTemplateKey | null => {
    const stateId = params.get("order_state_id");
    const event = params.get("email_event_type");

    if (stateId && Number.isInteger(Number(stateId))) {
        return { order_state_id: Number(stateId) };
    }

    if (event) {
        return { email_event_type: event as EmailEventType };
    }

    return null;
};

/**
 * /business/settings/email-templates — block-based editor for the supplier's customizable
 * emails: pick a slot on the left, arrange blocks in the middle, watch the server-rendered
 * preview on the right, then save, send yourself a test, or reset to the built-in email.
 */
export const EmailTemplatesPage = () => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);
    const [searchParams] = useSearchParams();
    const [showTheme, setShowTheme] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);

    const editor = useEmailTemplateEditor({
        userModel: userModel!,
        supplierModel: supplierModel!,
        initialKey: useMemo(() => keyFromSearch(searchParams), [searchParams]),
    });

    const notify = (message: string, level: NotificationLevelEnum, isFormatted = false) => {
        uiContext.notifyFn(null, { message, level, position: NotificationPositionEnum.bc }, isFormatted);
    };

    // Surface hook errors as notifications (raw server text, so mark as formatted).
    useEffect(() => {
        if (editor.error) {
            notify(editor.error, NotificationLevelEnum.error, true);
            editor.clearError();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor.error]);

    // Warn on tab close with unsaved changes.
    useEffect(() => {
        if (!editor.dirty || !editor.selected?.isSaved) {
            return;
        }

        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", handler);

        return () => window.removeEventListener("beforeunload", handler);
    }, [editor.dirty, editor.selected]);

    const crumbs: IBreadcrumbs[] = [
        { title: "business_settings", url: "/business/settings" },
        { title: "email_templates", url: "/business/settings/email-templates" },
    ];

    const leafBlockTypes = useMemo(() => {
        const all = editor.catalog?.blockTypes || [];
        const orderOnly = editor.catalog?.orderOnlyBlockTypes || [];

        return all.filter((t) => t !== EmailBlockTypeEnum.COLUMNS && (editor.scope === "order" || !orderOnly.includes(t)));
    }, [editor.catalog, editor.scope]);

    const selectedBlock =
        editor.document && editor.selectedBlockId ? findBlock(editor.document, editor.selectedBlockId)?.block || null : null;

    const t = (id: string) => intl.formatMessage({ id });

    const onAddBlock = (type: EmailBlockTypeEnum) => {
        const block = createBlock(type, t);
        editor.setDocument((doc) => insertBlock(doc, block));
        editor.setSelectedBlockId(block.id);
    };

    const onAddToColumn = (columnsId: string, columnIndex: 0 | 1, type: EmailBlockTypeEnum) => {
        const block = createBlock(type, t) as IEmailLeafBlock;
        editor.setDocument((doc) => addToColumn(doc, columnsId, columnIndex, block));
        editor.setSelectedBlockId(block.id);
    };

    const onDuplicate = (id: string) => {
        if (!editor.document) {
            return;
        }

        // Compute outside the updater: setState updaters must stay side-effect free.
        const result = duplicateBlock(editor.document, id);
        editor.setDocument(result.doc);

        if (result.newId) {
            editor.setSelectedBlockId(result.newId);
        }
    };

    const onRemove = (id: string) => {
        editor.setDocument((doc) => removeBlock(doc, id));
        if (editor.selectedBlockId === id) {
            editor.setSelectedBlockId(null);
        }
    };

    const onPatchBlock = (patch: Partial<IEmailBlock>) => {
        if (editor.selectedBlockId) {
            const id = editor.selectedBlockId;
            editor.setDocument((doc) => updateBlock(doc, id, patch));
        }
    };

    const onSave = async () => {
        if (await editor.save()) {
            notify("email_template_saved", NotificationLevelEnum.success);
        }
    };

    const onSendTest = async () => {
        const result = await editor.sendTest();

        if (result) {
            notify(
                intl.formatMessage({ id: result.messageKey, defaultMessage: result.message }, { to: result.to }),
                result.sent ? NotificationLevelEnum.success : NotificationLevelEnum.warning,
                true
            );
        }
    };

    const onResetToDefault = async () => {
        setConfirmReset(false);

        if (await editor.resetToDefault()) {
            notify("email_template_reset", NotificationLevelEnum.success);
        }
    };

    const selectedListKey = editor.selectedKey
        ? editor.selectedKey.order_state_id
            ? `state:${editor.selectedKey.order_state_id}`
            : `event:${editor.selectedKey.email_event_type}`
        : null;

    const busy = editor.saving || editor.templateLoading;

    const onSelectTemplate = (t: IEmailTemplateSummaryDTO) => {
        if (editor.dirty && editor.selected?.isSaved) {
            if (!window.confirm(intl.formatMessage({ id: "email_template_discard_changes_confirm" }))) {
                return;
            }
        }

        editor.selectTemplate({ order_state_id: t.order_state_id, email_event_type: t.email_event_type });
    };

    return (
        <div className={"EmailTemplatesPage"}>
            <Breadcrumbs crumbs={crumbs} />

            <div className={"d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2"}>
                <OwTitle title={"email_templates"} leftIconName={"fad fa-envelope-open-text"} />
                <div className={"d-flex align-items-center flex-wrap gap-2"}>
                    {editor.dirty && editor.selected?.isSaved ? (
                        <>
                            <OwLabel
                                leftIconClass={"fad fa-exclamation-triangle"}
                                title={"pending_changes"}
                                noBottomMargin={true}
                                className={"text-danger me-2"}
                            />
                            <OwButton
                                leftIconClass={"fas fa-undo"}
                                title={"reset"}
                                fn={editor.revert}
                                className={"btn-warning"}
                                disabled={busy}
                            />
                        </>
                    ) : null}
                    <OwButton
                        leftIconClass={"fad fa-paper-plane"}
                        title={"send_test_email"}
                        fn={onSendTest}
                        className={"btn-outline-secondary"}
                        disabled={busy || !editor.document}
                    />
                    {editor.selected?.isSaved && editor.selected.template.guid ? (
                        confirmReset ? (
                            <>
                                <OwButton
                                    title={"confirm_reset_to_default"}
                                    fn={onResetToDefault}
                                    className={"btn-danger"}
                                    disabled={busy}
                                />
                                <OwButton title={"cancel"} fn={() => setConfirmReset(false)} className={"btn-outline-secondary"} />
                            </>
                        ) : (
                            <OwButton
                                leftIconClass={"fad fa-trash-restore"}
                                title={"reset_to_default"}
                                fn={() => setConfirmReset(true)}
                                className={"btn-outline-danger"}
                                disabled={busy}
                            />
                        )
                    ) : null}
                    <OwButton
                        id={"save-email-template"}
                        title={"save"}
                        leftIconClass={"fad fa-save"}
                        fn={onSave}
                        active={editor.saving}
                        disabled={busy || !editor.dirty}
                    />
                </div>
            </div>

            {editor.loading ? (
                <ActivityIndicator active={true} />
            ) : (
                <div className={"email-template-layout"}>
                    <div className={"list-section section-card"}>
                        <EmailTemplateList
                            templates={editor.templates}
                            selectedKey={selectedListKey}
                            disabled={busy}
                            onSelect={onSelectTemplate}
                        />
                    </div>

                    <div className={"editor-section"}>
                        {editor.selected && editor.document ? (
                            <>
                                <div className={"section-card"}>
                                    <div className={"d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2"}>
                                        <div>
                                            <OwLabel title={"email_details"} isBold={true} noBottomMargin={true} />
                                            <div className={"form-text mt-0"}>
                                                <OwTranslate
                                                    id={editor.selected.template.labelKey}
                                                    defaultMessage={editor.selected.template.label}
                                                />
                                            </div>
                                        </div>
                                        <OwToggle
                                            name={"is_active"}
                                            label={"active"}
                                            active={editor.isActive}
                                            disabled={busy}
                                            fn={(e: any) => editor.setIsActive(!!e.target.value)}
                                            labelOnRight={true}
                                        />
                                    </div>
                                    {editor.selected.template.group === "workflow" && editor.selected.template.sendsEmail === false ? (
                                        <div className={"alert alert-warning py-2 small"}>
                                            {intl.formatMessage({ id: "email_template_step_not_sending_hint" })}
                                        </div>
                                    ) : null}
                                    {editor.selected.template.workflows && editor.selected.template.workflows.length > 1 ? (
                                        <div className={"form-text mb-2"}>
                                            {intl.formatMessage({ id: "email_template_workflows_hint" })}:{" "}
                                            {editor.selected.template.workflows
                                                .map((w) => (w === "Default" ? intl.formatMessage({ id: "default" }) : w))
                                                .join(", ")}
                                        </div>
                                    ) : null}
                                    {!editor.selected.isSaved ? (
                                        <div className={"alert alert-info py-2 small"}>
                                            {intl.formatMessage({ id: "email_template_not_saved_hint" })}
                                        </div>
                                    ) : null}
                                    {editor.selected.warnings.length ? (
                                        <div className={"alert alert-warning py-2 small"}>
                                            {editor.selected.warnings.map((w, i) => (
                                                <div key={i}>{intl.formatMessage({ id: w, defaultMessage: w })}</div>
                                            ))}
                                        </div>
                                    ) : null}
                                    <div className={"mb-2"}>
                                        <label className={"form-label small mb-0"} htmlFor={"tpl-name"}>
                                            {intl.formatMessage({ id: "name" })}
                                        </label>
                                        <input
                                            id={"tpl-name"}
                                            type={"text"}
                                            className={"form-control form-control-sm"}
                                            value={editor.name}
                                            maxLength={200}
                                            disabled={busy}
                                            onChange={(e) => editor.setName(e.target.value)}
                                        />
                                    </div>
                                    <div className={"mb-2"}>
                                        <label className={"form-label small mb-0"} htmlFor={"tpl-subject"}>
                                            {intl.formatMessage({ id: "subject" })}
                                        </label>
                                        <div>
                                            <input
                                                id={"tpl-subject"}
                                                type={"text"}
                                                className={"form-control form-control-sm"}
                                                value={editor.document.subject}
                                                maxLength={255}
                                                disabled={busy}
                                                onChange={(e) => {
                                                    const subject = e.target.value;
                                                    editor.setDocument((doc) => ({ ...doc, subject }));
                                                }}
                                            />
                                            <div className={"d-flex justify-content-end mt-1"}>
                                                <MergeVarPicker
                                                    mergeVars={editor.catalog?.mergeVars || []}
                                                    scope={editor.scope}
                                                    disabled={busy}
                                                    className={"w-auto"}
                                                    onInsert={(v) =>
                                                        editor.setDocument((doc) => ({ ...doc, subject: `${doc.subject}${v}` }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={"form-label small mb-0"} htmlFor={"tpl-preheader"}>
                                            {intl.formatMessage({ id: "preheader" })}
                                        </label>
                                        <input
                                            id={"tpl-preheader"}
                                            type={"text"}
                                            className={"form-control form-control-sm"}
                                            value={editor.document.preheader || ""}
                                            maxLength={255}
                                            disabled={busy}
                                            placeholder={intl.formatMessage({ id: "preheader_hint" })}
                                            onChange={(e) => {
                                                const preheader = e.target.value || undefined;
                                                editor.setDocument((doc) => ({ ...doc, preheader }));
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className={"section-card"}>
                                    <div className={"d-flex align-items-center justify-content-between mb-2"}>
                                        <OwLabel title={"add_block"} isBold={true} noBottomMargin={true} />
                                        <button type={"button"} className={"btn btn-sm btn-link"} onClick={() => setShowTheme((s) => !s)}>
                                            <i className={"fad fa-palette me-1"} aria-hidden={true} />
                                            {intl.formatMessage({ id: "theme" })}
                                        </button>
                                    </div>
                                    <BlockPalette
                                        blockTypes={editor.catalog?.blockTypes || []}
                                        orderOnlyBlockTypes={editor.catalog?.orderOnlyBlockTypes || []}
                                        scope={editor.scope}
                                        onAdd={onAddBlock}
                                        disabled={busy}
                                    />
                                    {showTheme ? (
                                        <div className={"mt-3"}>
                                            <ThemePanel
                                                theme={editor.document.theme}
                                                disabled={busy}
                                                onChange={(theme) => editor.setDocument((doc) => ({ ...doc, theme }))}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <div className={"section-card"}>
                                    <OwLabel title={"layout"} isBold={true} />
                                    <BlockCanvas
                                        document={editor.document}
                                        selectedBlockId={editor.selectedBlockId}
                                        leafBlockTypes={leafBlockTypes}
                                        disabled={busy}
                                        onChange={(next) => editor.setDocument(next)}
                                        onSelect={editor.setSelectedBlockId}
                                        onDuplicate={onDuplicate}
                                        onRemove={onRemove}
                                        onAddToColumn={onAddToColumn}
                                    />
                                </div>

                                {selectedBlock ? (
                                    <div className={"section-card"}>
                                        <BlockPropertyPanel
                                            key={selectedBlock.id}
                                            block={selectedBlock}
                                            mergeVars={editor.catalog?.mergeVars || []}
                                            orderItemTableColumns={editor.catalog?.orderItemTableColumns || []}
                                            scope={editor.scope}
                                            disabled={busy}
                                            onChange={onPatchBlock}
                                            onClose={() => editor.setSelectedBlockId(null)}
                                        />
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className={"section-card text-muted"}>{intl.formatMessage({ id: "select_email_template_hint" })}</div>
                        )}
                    </div>

                    <div className={"preview-section section-card"}>
                        <EmailPreviewPane
                            preview={editor.preview}
                            loading={editor.previewLoading}
                            error={editor.previewError}
                            mode={editor.previewMode}
                            onModeChange={editor.setPreviewMode}
                            scope={editor.scope}
                            isQuote={editor.isQuoteSlot}
                            sampleOrders={editor.sampleOrders}
                            sampleOrderGuid={editor.sampleOrderGuid}
                            onSampleOrderChange={editor.setSampleOrderGuid}
                            onSearchSampleOrders={editor.searchSampleOrders}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTemplatesPage;
